import _ from 'lodash'
import Sifter from 'sifter'

/**
 * We override the sifter when only searching for a single letter
 * https://github.com/SpiderStrategies/Scoreboard/issues/21752#issuecomment-454191301
 */
class ScoreboardSifter extends Sifter {

	/**
	 * Textually searches arrays and hashes of objects
	 * by property (or multiple properties). Designed
	 * specifically for autocomplete.
	 *
	 * @constructor
	 *
	 * @param {array|object} items
	 *
	 * @param {Object} [options.fieldAdapters] Hash of fieldLabel:adapterFun.  The
	 * adapter function will be invoked with the value of the field before any
	 * sort/score calculation is performed.
	 *
	 * @param {object} [settings] Sifter settings
	 */
	constructor(items = [], options = {}, settings) {
		super(items, settings)
		this.options = options
		this._allItems = items.map((item, i) => {
			// sifter results are apparently based on the items' initial indexes,
			// so we need to keep track of them
			return _.extend(_.cloneDeep(item), { _itemIndex: i })
		})
	}

	/**
	 *
	 * @param {String} searchStr
	 *
	 * @param {Boolean} options.preSorted=false If true, no sorting will be applied
	 * to the results (e.g. multi-principal-chooser fixed group sort; see selects.js#sortOptions)
	 */
	search(searchStr, options) {

		const sifterResult = (results) => {
			// Mirrors return object of sifter
			return {
				query: searchStr,
				options,
				total: _.size(results),
				// sifter results are apparently based on the items' initial indexes
				items: results.map(item => { return { id: item._itemIndex } })
			}
		}

		if (options.preSorted) {
			// #35247
			return sifterResult(this._allItems)
		} else if (_.size(searchStr) === 1) {
			// #21752 Bypass sifter scoring algorithm and just sort alphabetically

			const letter = _.toLower(searchStr)

			let results = this._allItems.filter(item => {
				// #31674 Return any items that contain the typed letter in a search field
				let foundMatch = _.find(options.fields, field => {
					return _.indexOf(_.toLower(item[field]), _.toLower(letter)) != -1
				})
				return foundMatch
			})

			// #24544 Simply sort by the configured sort field(s), instead of
			// by sifter's "quality" of match
			let sortFields = options.sort
			if (!sortFields) {
				// For single-letter search, only look at the first non-id field
				// (usually "label", but it can be something else)
				let fallbackField = _.find(options.fields, f => f !== 'id')
				sortFields = [ { field: fallbackField, direction: 'asc'} ]
			}

			sort(results, sortFields)
			return sifterResult(results)
		} else {
			return super.search(searchStr, options)
		}
	}

	/**
	 * Adapted from https://github.com/brianreavis/sifter.js/blob/master/lib/sifter.js#L117
	 * (needed to override the private `getattr` function)
	 *
	 * Returns a function to be used to score individual results.
	 *
	 * Good matches will have a higher score than poor matches.
	 * If an item is not a match, 0 will be returned by the function.
	 *
	 * @param {object|string} search
	 * @param {object} options (optional)
	 * @returns {function}
	 */
	getScoreFunction(search, options) {
		var self, fields, tokens, token_count, nesting

		self        = this
		search      = self.prepareSearch(search, options)
		tokens      = search.tokens
		fields      = search.options.fields
		token_count = tokens.length
		nesting     = search.options.nesting

		/**
		 * Calculates how close of a match the
		 * given value is against a search token.
		 *
		 * @param {mixed} value
		 * @param {object} token
		 * @return {number}
		 */
		var scoreValue = function(value, token) {
			var score, pos

			if (!value) return 0
			value = String(value || '')
			pos = value.search(token.regex)
			if (pos === -1) return 0
			score = token.string.length / value.length
			if (pos === 0) score += 0.5
			return score
		}

		/**
		 * A property getter resolving dot-notation
		 * @param  {Object}  obj     The root object to fetch property on
		 * @param  {String}  name    The optionally dotted property name to fetch
		 * @param  {Boolean} nesting Handle nesting or not
		 * @return {Object}          The resolved property value
		 */
		const getattr = (obj, name, nesting) => {
			if (!obj || !name) {
				return
			}
			const data = obj
			const {fieldAdapters = {}} = this.options
			const adapter = fieldAdapters[name] || _.identity
			if (!nesting) {
				return adapter(obj[name], data)
			}
			const names = name.split('.')
			if (names.length && (obj = obj[names.shift()])) {
				return adapter(obj, data)
			}
		}

		/**
		 * Calculates the score of an object
		 * against the search query.
		 *
		 * @param {object} token
		 * @param {object} data
		 * @return {number}
		 */
		var scoreObject = (function() {
			var field_count = fields.length
			if (!field_count) {
				return function() { return 0 }
			}
			if (field_count === 1) {
				return function(token, data) {
					return scoreValue(getattr(data, fields[0], nesting), token)
				}
			}
			return function(token, data) {
				for (var i = 0, sum = 0; i < field_count; i++) {
					sum += scoreValue(getattr(data, fields[i], nesting), token)
				}
				return sum / field_count
			}
		})()

		if (!token_count) {
			return function() { return 0 }
		}
		if (token_count === 1) {
			return function(data) {
				return scoreObject(tokens[0], data)
			}
		}

		if (search.options.conjunction === 'and') {
			return function(data) {
				var score
				for (var i = 0, sum = 0; i < token_count; i++) {
					score = scoreObject(tokens[i], data)
					if (score <= 0) return 0
					sum += score
				}
				return sum / token_count
			}
		} else {
			return function(data) {
				for (var i = 0, sum = 0; i < token_count; i++) {
					sum += scoreObject(tokens[i], data)
				}
				return sum / token_count
			}
		}
	}

}

/**
 * Sorts the given items in-place using the give sort fields (same format that
 * sifter uses)
 */
function sort (items, sortFields) {
	if (_.isEmpty(sortFields)) {
		return
	}

	items.sort((a, b) => {
		// #24776 need to put 'headerless' group up at the top
		if (a.group === 'headerless' || b.group === 'headerless') {
			if (a.group !== b.group) {
				if (a.group === 'headerless') {
					return -1
				}

				return 1
			}
		}

		var i
		for (i = 0; i < sortFields.length; i++) {
			let field = sortFields[i]

			// Compare the value for the current field
			let aVal = a[field.field]
			let bVal = b[field.field]

			if (aVal == bVal) {
				// If they are the same, try the next field
				continue
			}

			// If using descending order, need to flip the logic for which item
			// comes first
			let multiplier = field.direction === 'desc' ? -1 : 1

			if (aVal < bVal) {
				return -1 * multiplier
			}

			return 1 * multiplier
		}
	})
}

ScoreboardSifter.sort = sort

export default ScoreboardSifter
ScoreboardSifter.__filename__ = 'components/scoreboard-sifter.js'
